/**
 * device/apps/catalog.ts — Catalog / Shop App
 *
 * A polished in-game shop browser drawn entirely on Canvas 2D.
 * Three tabbed categories — Furniture, Clothing, Materials — each
 * with scrollable item cards showing name, rarity, price, and a
 * brief description. The player's coin balance is always visible at
 * the top, and items beyond their means are gently dimmed.
 */

import type { GameState } from "../../engine/state.js";
import type { DeviceApp, Rect } from "../app.js";
import { registerApp, drawRoundRect, drawText } from "../app.js";
import { getFurnitureType, renderFurniture } from "../../room/furniture.js";

// ─── Types ──────────────────────────────────────────────────────────────────

type Rarity = "common" | "uncommon" | "rare" | "legendary" | "seasonal";
type Category = "furniture" | "clothing" | "materials";

interface CatalogItem {
  name: string;
  price: number;
  rarity: Rarity;
  description: string;
  /** Accent color for the icon square */
  iconColor: string;
  /** Furniture registry ID — when set, renders actual isometric sprite */
  furnitureId?: string;
}

interface CatalogState {
  activeTab: Category;
  scrollY: number;
  /** Momentum velocity (px/s) for inertia scroll */
  scrollVelocity: number;
  /** Max scroll value computed during draw */
  maxScrollY: number;
  /** Cached content width for tap hit-testing (set each frame during draw) */
  cachedW: number;
  /** Cached content height for scroll calculation */
  cachedH: number;
}

// ─── Rarity Colors ──────────────────────────────────────────────────────────

const RARITY_COLORS: Record<Rarity, string> = {
  common:    "#d4d4d8",
  uncommon:  "#4ade80",
  rare:      "#60a5fa",
  legendary: "#c084fc",
  seasonal:  "#fbbf24",
};

const RARITY_LABELS: Record<Rarity, string> = {
  common:    "Common",
  uncommon:  "Uncommon",
  rare:      "Rare",
  legendary: "Legendary",
  seasonal:  "Seasonal",
};

// ─── Item Data ──────────────────────────────────────────────────────────────

const FURNITURE: CatalogItem[] = [
  { name: "Cozy Armchair",       price: 150,  rarity: "common",    iconColor: "#a78bfa", description: "Sink in and never leave.",             furnitureId: "chair_wooden" },
  { name: "Crystal Lamp",        price: 300,  rarity: "uncommon",  iconColor: "#67e8f9", description: "Refracts light into soft rainbows.",   furnitureId: "floor_lamp" },
  { name: "Enchanted Bookshelf", price: 800,  rarity: "rare",      iconColor: "#818cf8", description: "The pages turn themselves at night.",  furnitureId: "bookshelf" },
  { name: "Celestial Rug",       price: 2000, rarity: "legendary", iconColor: "#e879f9", description: "Woven from threads of starlight.",    furnitureId: "rug_woven" },
  { name: "Spring Blossom Vase", price: 500,  rarity: "seasonal",  iconColor: "#fb923c", description: "Petals drift in an invisible breeze.", furnitureId: "potted_plant" },
];

const CLOTHING: CatalogItem[] = [
  { name: "Silk Scarf",       price: 100,  rarity: "common",    iconColor: "#fca5a5", description: "Light as a whisper, warm as a hug." },
  { name: "Moonweave Cloak",  price: 600,  rarity: "rare",      iconColor: "#93c5fd", description: "Shimmers faintly under moonlight." },
  { name: "Starlight Crown",  price: 1500, rarity: "legendary", iconColor: "#d8b4fe", description: "A circlet that never dims." },
  { name: "Autumn Leaf Pin",  price: 250,  rarity: "seasonal",  iconColor: "#fdba74", description: "Changes color with the wind." },
  { name: "Canvas Bag",       price: 80,   rarity: "common",    iconColor: "#d6d3d1", description: "Sturdy, simple, reliable." },
  { name: "Velvet Gloves",    price: 200,  rarity: "uncommon",  iconColor: "#c4b5fd", description: "Impossibly soft to the touch." },
];

const MATERIALS: CatalogItem[] = [
  { name: "Oak Wood",      price: 10,   rarity: "common",    iconColor: "#a3836a", description: "Solid heartwood, good for crafting." },
  { name: "Silver Thread",  price: 50,   rarity: "uncommon",  iconColor: "#cbd5e1", description: "Conducts enchantment easily." },
  { name: "Moonstone",      price: 200,  rarity: "rare",      iconColor: "#a5b4fc", description: "Glows softly in darkness." },
  { name: "Dragon Scale",   price: 1000, rarity: "legendary", iconColor: "#f87171", description: "Warm to the touch, indestructible." },
  { name: "Frost Crystal",  price: 150,  rarity: "seasonal",  iconColor: "#7dd3fc", description: "Melts only in the hottest forge." },
];

const ITEMS_BY_CATEGORY: Record<Category, CatalogItem[]> = {
  furniture: FURNITURE,
  clothing:  CLOTHING,
  materials: MATERIALS,
};

// ─── Tabs ───────────────────────────────────────────────────────────────────

interface TabDef {
  key: Category;
  label: string;
}

const TABS: TabDef[] = [
  { key: "furniture", label: "Furniture" },
  { key: "clothing",  label: "Clothing" },
  { key: "materials", label: "Materials" },
];

// ─── Layout Constants ───────────────────────────────────────────────────────

const BALANCE_BAR_H = 24;
const TAB_BAR_H = 22;
const CARD_HEIGHT = 44;
const CARD_GAP = 4;
const CARD_MARGIN_X = 6;
const ICON_SIZE = 30;
const ICON_MARGIN = 6;

// ─── Persistent State Helper ────────────────────────────────────────────────

function getState(state: GameState): CatalogState {
  if (!state.player.device.appStates["catalog"]) {
    state.player.device.appStates["catalog"] = {
      activeTab: "furniture" as Category,
      scrollY: 0,
      scrollVelocity: 0,
      maxScrollY: 0,
      cachedW: 172,
      cachedH: 240,
    } satisfies CatalogState;
  }
  return state.player.device.appStates["catalog"] as CatalogState;
}

// ─── Drawing Helpers ────────────────────────────────────────────────────────

/** Draw a small coin icon — filled circle with a "C" */
function drawCoinIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
): void {
  // Outer ring
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = "#f59e0b";
  ctx.fill();

  // Inner highlight
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 0.7, 0, Math.PI * 2);
  ctx.fillStyle = "#fbbf24";
  ctx.fill();

  // "C" letter
  ctx.font = `bold ${Math.round(radius * 1.2)}px sans-serif`;
  ctx.fillStyle = "#92400e";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("C", cx, cy + 0.5);
}

/** Draw a rarity dot */
function drawRarityDot(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  rarity: Rarity,
): void {
  const color = RARITY_COLORS[rarity];

  // Glow for legendary / seasonal
  if (rarity === "legendary" || rarity === "seasonal") {
    ctx.beginPath();
    ctx.arc(cx, cy, radius + 2, 0, Math.PI * 2);
    ctx.fillStyle = adjustAlpha(color, 0.25);
    ctx.fill();
  }

  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}

/** Draw an item card */
function drawItemCard(
  ctx: CanvasRenderingContext2D,
  item: CatalogItem,
  x: number,
  y: number,
  w: number,
  canAfford: boolean,
): void {
  const alpha = canAfford ? 1.0 : 0.4;

  // Card background
  drawRoundRect(ctx, x, y, w, CARD_HEIGHT, 6, `rgba(30,30,34,${alpha})`, `rgba(60,60,66,${alpha * 0.5})`);

  // ── Icon square (left side) — procedural item preview ────────────
  const iconX = x + ICON_MARGIN;
  const iconY = y + (CARD_HEIGHT - ICON_SIZE) / 2;
  const iconAlpha = canAfford ? 0.9 : 0.35;

  // Dark inset background
  drawRoundRect(ctx, iconX, iconY, ICON_SIZE, ICON_SIZE, 5, adjustAlpha("#1a1408", iconAlpha));

  // Draw the item-specific preview
  ctx.globalAlpha = iconAlpha;
  drawCatalogItemIcon(ctx, iconX + ICON_SIZE / 2, iconY + ICON_SIZE / 2, ICON_SIZE, item);
  ctx.globalAlpha = 1;

  // ── Text area ───────────────────────────────────────────────────
  const textX = iconX + ICON_SIZE + 8;
  const textW = w - ICON_SIZE - ICON_MARGIN * 2 - 12;

  // Item name
  const nameColor = canAfford ? "#f5e6d0" : `rgba(245,230,208,${alpha})`;
  drawText(ctx, item.name, textX, y + 5, textW - 50, "bold 9px sans-serif", nameColor);

  // Rarity dot + label (to the right of name)
  const rarityDotX = textX + textW - 36;
  const rarityDotY = y + 11;
  drawRarityDot(ctx, rarityDotX, rarityDotY, 3, item.rarity);
  drawText(
    ctx,
    RARITY_LABELS[item.rarity],
    rarityDotX + 6, y + 6, 40,
    "7px sans-serif",
    adjustAlpha(RARITY_COLORS[item.rarity], canAfford ? 0.85 : 0.35),
  );

  // Price row — coin icon + number
  const priceY = y + 19;
  drawCoinIcon(ctx, textX + 5, priceY + 4, 4);
  const priceColor = canAfford ? "#fbbf24" : `rgba(251,191,36,${alpha})`;
  drawText(ctx, `${item.price}`, textX + 14, priceY, 50, "bold 8px sans-serif", priceColor);

  // Description
  const descColor = canAfford ? "rgba(161,161,170,0.85)" : `rgba(161,161,170,${alpha * 0.7})`;
  drawText(ctx, item.description, textX, y + 31, textW, "7px sans-serif", descColor);
}

// ─── The App ────────────────────────────────────────────────────────────────

const catalogApp: DeviceApp = {
  id: "catalog",
  name: "Catalog",
  iconGradient: ["#f97316", "#ea580c"],

  drawIcon(ctx, cx, cy, size) {
    // Shopping bag shape — rectangle body + two handle arcs
    const bagW = size * 0.42;
    const bagH = size * 0.44;
    const bagX = cx - bagW / 2;
    const bagY = cy - bagH / 2 + size * 0.06;

    ctx.strokeStyle = "#ffffff";
    ctx.fillStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 1.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Bag body (rounded rect)
    ctx.beginPath();
    const r = 2;
    ctx.moveTo(bagX + r, bagY);
    ctx.lineTo(bagX + bagW - r, bagY);
    ctx.arcTo(bagX + bagW, bagY, bagX + bagW, bagY + r, r);
    ctx.lineTo(bagX + bagW, bagY + bagH - r);
    ctx.arcTo(bagX + bagW, bagY + bagH, bagX + bagW - r, bagY + bagH, r);
    ctx.lineTo(bagX + r, bagY + bagH);
    ctx.arcTo(bagX, bagY + bagH, bagX, bagY + bagH - r, r);
    ctx.lineTo(bagX, bagY + r);
    ctx.arcTo(bagX, bagY, bagX + r, bagY, r);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Handle (arc above the bag)
    const handleW = bagW * 0.45;
    ctx.beginPath();
    ctx.arc(cx, bagY, handleW, Math.PI, 0, false);
    ctx.stroke();
  },

  drawContent(ctx, rect, state, dt) {
    const { x, y, w, h } = rect;
    const cs = getState(state);
    cs.cachedW = w;
    cs.cachedH = h;
    const coins = state.player.currency;

    // ── Balance Bar ─────────────────────────────────────────────────
    const balY = y;
    drawRoundRect(ctx, x, balY, w, BALANCE_BAR_H, 0, "#1a1408");

    // Coin icon + balance
    drawCoinIcon(ctx, x + 16, balY + BALANCE_BAR_H / 2, 6);
    drawText(
      ctx,
      `${coins}`,
      x + 28,
      balY + BALANCE_BAR_H / 2 - 5,
      80,
      "bold 11px sans-serif",
      "#fbbf24",
    );

    // "Your Balance" label
    drawText(
      ctx,
      "Your Balance",
      x + w - 8,
      balY + BALANCE_BAR_H / 2 - 4,
      80,
      "8px sans-serif",
      "rgba(161,161,170,0.7)",
      "right",
    );

    // ── Tab Bar ─────────────────────────────────────────────────────
    const tabY = balY + BALANCE_BAR_H + 2;
    const tabW = w / TABS.length;

    for (let i = 0; i < TABS.length; i++) {
      const tab = TABS[i];
      const tx = x + i * tabW;
      const isActive = tab.key === cs.activeTab;

      // Tab background
      if (isActive) {
        drawRoundRect(ctx, tx + 2, tabY + 1, tabW - 4, TAB_BAR_H - 2, 4, "rgba(249,115,22,0.2)");
      }

      // Tab label
      const labelColor = isActive ? "#fb923c" : "rgba(200,168,120,0.6)";
      const labelFont = isActive ? "bold 8px sans-serif" : "8px sans-serif";
      drawText(ctx, tab.label, tx + tabW / 2, tabY + 6, tabW - 8, labelFont, labelColor, "center");

      // Active indicator bar
      if (isActive) {
        drawRoundRect(ctx, tx + tabW * 0.2, tabY + TAB_BAR_H - 2, tabW * 0.6, 2, 1, "#f97316");
      }
    }

    // Separator below tabs
    ctx.fillStyle = "rgba(60,60,66,0.5)";
    ctx.fillRect(x, tabY + TAB_BAR_H, w, 0.5);

    // ── Item List ───────────────────────────────────────────────────
    const listY = tabY + TAB_BAR_H + 4;
    const listH = h - (listY - y);
    const items = ITEMS_BY_CATEGORY[cs.activeTab];

    // Momentum / inertia scroll
    const totalContentH = items.length * (CARD_HEIGHT + CARD_GAP);
    const maxScroll = Math.max(0, totalContentH - listH);
    cs.maxScrollY = maxScroll;
    if (Math.abs(cs.scrollVelocity) > 0.5) {
      cs.scrollY += cs.scrollVelocity * dt;
      cs.scrollVelocity *= Math.exp(-6 * dt);
    } else {
      cs.scrollVelocity = 0;
    }
    cs.scrollY = Math.max(0, Math.min(cs.scrollY, maxScroll));

    // Clip to list area
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, listY, w, listH);
    ctx.clip();

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const cardY = listY - cs.scrollY + i * (CARD_HEIGHT + CARD_GAP);

      // Skip cards completely outside the clip
      if (cardY + CARD_HEIGHT < listY || cardY > listY + listH) continue;

      const canAfford = coins >= item.price;
      drawItemCard(ctx, item, x + CARD_MARGIN_X, cardY, w - CARD_MARGIN_X * 2, canAfford);
    }

    ctx.restore();

    // ── Scroll Indicator ────────────────────────────────────────────
    if (totalContentH > listH) {
      const scrollBarH = Math.max(12, (listH / totalContentH) * listH);
      const scrollBarY = listY + (cs.scrollY / totalContentH) * listH;
      const scrollBarX = x + w - 3;

      drawRoundRect(ctx, scrollBarX, scrollBarY, 2, scrollBarH, 1, "rgba(161,161,170,0.25)");
    }
  },

  handleTap(localX, localY, state) {
    const cs = getState(state);

    // ── Tab bar tap ─────────────────────────────────────────────────
    const tabY = BALANCE_BAR_H + 2;
    if (localY >= tabY && localY < tabY + TAB_BAR_H) {
      const tabW = cs.cachedW / TABS.length;
      const tabIdx = Math.min(TABS.length - 1, Math.max(0, Math.floor(localX / tabW)));
      cs.activeTab = TABS[tabIdx].key;
      cs.scrollY = 0;
      return true;
    }

    // ── Item card tap — buy item ──────────────────────────────────────
    const listY = tabY + TAB_BAR_H + 4;
    if (localY >= listY) {
      const items = ITEMS_BY_CATEGORY[cs.activeTab];
      const relY = localY - listY + cs.scrollY;
      const cardStep = CARD_HEIGHT + CARD_GAP;
      const cardIdx = Math.floor(relY / cardStep);
      if (cardIdx >= 0 && cardIdx < items.length) {
        const item = items[cardIdx];
        if (state.player.currency >= item.price) {
          state.player.currency -= item.price;
          state.player.inventory.push({
            id: `item_${Date.now()}`,
            type: item.furnitureId ? "furniture" : "clothing",
            name: item.name,
            rarity: item.rarity,
            quantity: 1,
            acquiredAt: Date.now(),
            furnitureTypeId: item.furnitureId,
          });
        }
        return true;
      }
    }

    return false;
  },

  handleDrag(dy, state) {
    const cs = getState(state);
    cs.scrollY = Math.max(0, Math.min(cs.scrollY + dy, cs.maxScrollY));
    cs.scrollVelocity = 0;
  },

  handleDragEnd(velocityY, state) {
    const cs = getState(state);
    cs.scrollVelocity = velocityY;
  },

  onOpen(state) {
    const cs = getState(state);
    cs.scrollY = 0;
    cs.scrollVelocity = 0;
  },

  badge(_state) {
    return 0;
  },
};

// ─── Item Preview Icons ────────────────────────────────────────────────────

/**
 * Draw a recognizable procedural preview for each catalog item.
 * Furniture gets a tiny iso silhouette, clothing gets garment shapes,
 * materials get crystal/gem forms — all tinted with the item's accent color.
 */
function drawCatalogItemIcon(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number,
  item: CatalogItem,
): void {
  // ── Actual furniture sprite rendering ───────────────────────────────
  if (item.furnitureId) {
    const ft = getFurnitureType(item.furnitureId);
    if (ft) {
      ctx.save();
      // Scale the furniture sprite to fit the icon area
      const scale = size / Math.max(ft.sprite.width, ft.sprite.height, 48) * 0.65;
      ctx.translate(cx, cy + size * 0.15);
      ctx.scale(scale, scale);
      renderFurniture(ctx, ft, 0, 0, 0, { on: true, lit: true }, 0);
      ctx.restore();
      return;
    }
  }

  const s = size * 0.32;
  const c = item.iconColor;
  ctx.fillStyle = c;
  ctx.strokeStyle = c;
  ctx.lineWidth = 1;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const name = item.name.toLowerCase();

  // ── Furniture previews ──────────────────────────────────────────────
  if (name.includes("chair") || name.includes("armchair")) {
    // Iso chair
    ctx.fillStyle = c;
    ctx.fillRect(cx - s * 0.5, cy - s * 0.1, s * 1, s * 0.5); // seat
    ctx.fillRect(cx - s * 0.55, cy - s * 0.8, s * 0.12, s * 0.7); // left back
    ctx.fillRect(cx + s * 0.43, cy - s * 0.8, s * 0.12, s * 0.7); // right back
    ctx.fillStyle = adjustAlpha(c, 0.7);
    ctx.fillRect(cx - s * 0.5, cy + s * 0.4, s * 0.15, s * 0.4); // leg L
    ctx.fillRect(cx + s * 0.35, cy + s * 0.4, s * 0.15, s * 0.4); // leg R
  } else if (name.includes("lamp")) {
    // Lamp with glow
    ctx.fillStyle = adjustAlpha(c, 0.3);
    ctx.beginPath();
    ctx.arc(cx, cy - s * 0.4, s * 0.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = c;
    // Shade
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.4, cy - s * 0.3);
    ctx.lineTo(cx - s * 0.25, cy - s * 0.7);
    ctx.lineTo(cx + s * 0.25, cy - s * 0.7);
    ctx.lineTo(cx + s * 0.4, cy - s * 0.3);
    ctx.closePath();
    ctx.fill();
    // Pole
    ctx.fillRect(cx - s * 0.06, cy - s * 0.3, s * 0.12, s * 0.8);
    // Base
    ctx.fillRect(cx - s * 0.25, cy + s * 0.5, s * 0.5, s * 0.12);
  } else if (name.includes("shelf") || name.includes("bookshelf")) {
    ctx.fillStyle = adjustAlpha(c, 0.6);
    ctx.fillRect(cx - s * 0.55, cy - s * 0.8, s * 1.1, s * 1.6); // frame
    ctx.fillStyle = c;
    for (let row = 0; row < 3; row++) {
      const ry = cy - s * 0.6 + row * s * 0.5;
      ctx.fillRect(cx - s * 0.45, ry + s * 0.35, s * 0.9, s * 0.06); // shelf
      // Books
      for (let b = 0; b < 3; b++) {
        ctx.fillRect(cx - s * 0.4 + b * s * 0.3, ry, s * 0.22, s * 0.32);
      }
    }
  } else if (name.includes("rug")) {
    // Diamond rug
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 0.6);
    ctx.lineTo(cx + s * 0.8, cy);
    ctx.lineTo(cx, cy + s * 0.6);
    ctx.lineTo(cx - s * 0.8, cy);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = adjustAlpha(c, 0.5);
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 0.3);
    ctx.lineTo(cx + s * 0.4, cy);
    ctx.lineTo(cx, cy + s * 0.3);
    ctx.lineTo(cx - s * 0.4, cy);
    ctx.closePath();
    ctx.fill();
  } else if (name.includes("vase") || name.includes("plant")) {
    // Pot + leaves
    ctx.fillStyle = adjustAlpha(c, 0.8);
    ctx.fillRect(cx - s * 0.25, cy + s * 0.1, s * 0.5, s * 0.55);
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.ellipse(cx, cy - s * 0.2, s * 0.5, s * 0.4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = adjustAlpha(c, 0.7);
    ctx.beginPath();
    ctx.ellipse(cx - s * 0.3, cy - s * 0.5, s * 0.25, s * 0.3, -0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(cx + s * 0.25, cy - s * 0.45, s * 0.2, s * 0.25, 0.3, 0, Math.PI * 2);
    ctx.fill();
  // ── Clothing previews ──────────────────────────────────────────────
  } else if (name.includes("scarf")) {
    ctx.lineWidth = s * 0.2;
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.5, cy - s * 0.6);
    ctx.quadraticCurveTo(cx + s * 0.5, cy - s * 0.3, cx + s * 0.2, cy + s * 0.2);
    ctx.quadraticCurveTo(cx, cy + s * 0.8, cx - s * 0.3, cy + s * 0.7);
    ctx.stroke();
    ctx.lineWidth = 1;
  } else if (name.includes("cloak")) {
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.2, cy - s * 0.9);
    ctx.lineTo(cx - s * 0.7, cy + s * 0.8);
    ctx.quadraticCurveTo(cx, cy + s, cx + s * 0.7, cy + s * 0.8);
    ctx.lineTo(cx + s * 0.2, cy - s * 0.9);
    ctx.closePath();
    ctx.fill();
    // Clasp
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(cx, cy - s * 0.8, s * 0.1, 0, Math.PI * 2);
    ctx.fill();
  } else if (name.includes("crown")) {
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.6, cy + s * 0.15);
    ctx.lineTo(cx - s * 0.6, cy - s * 0.2);
    ctx.lineTo(cx - s * 0.3, cy);
    ctx.lineTo(cx, cy - s * 0.5);
    ctx.lineTo(cx + s * 0.3, cy);
    ctx.lineTo(cx + s * 0.6, cy - s * 0.2);
    ctx.lineTo(cx + s * 0.6, cy + s * 0.15);
    ctx.closePath();
    ctx.fill();
    // Band
    ctx.fillStyle = adjustAlpha(c, 0.6);
    ctx.fillRect(cx - s * 0.6, cy + s * 0.05, s * 1.2, s * 0.15);
  } else if (name.includes("pin") || name.includes("leaf")) {
    // Leaf shape
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 0.7);
    ctx.quadraticCurveTo(cx + s * 0.6, cy - s * 0.3, cx + s * 0.5, cy + s * 0.4);
    ctx.quadraticCurveTo(cx, cy + s * 0.6, cx - s * 0.5, cy + s * 0.4);
    ctx.quadraticCurveTo(cx - s * 0.6, cy - s * 0.3, cx, cy - s * 0.7);
    ctx.fill();
    // Stem
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 0.6);
    ctx.lineTo(cx, cy + s * 0.4);
    ctx.stroke();
  } else if (name.includes("bag")) {
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.4, cy - s * 0.5);
    ctx.lineTo(cx + s * 0.4, cy - s * 0.5);
    ctx.quadraticCurveTo(cx + s * 0.5, cy, cx + s * 0.4, cy + s * 0.6);
    ctx.lineTo(cx - s * 0.4, cy + s * 0.6);
    ctx.quadraticCurveTo(cx - s * 0.5, cy, cx - s * 0.4, cy - s * 0.5);
    ctx.fill();
    // Handle
    ctx.beginPath();
    ctx.arc(cx, cy - s * 0.6, s * 0.2, Math.PI, 0);
    ctx.stroke();
  } else if (name.includes("glove")) {
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.35, cy - s * 0.7);
    ctx.lineTo(cx + s * 0.35, cy - s * 0.7);
    ctx.lineTo(cx + s * 0.35, cy + s * 0.6);
    ctx.quadraticCurveTo(cx, cy + s * 0.8, cx - s * 0.35, cy + s * 0.6);
    ctx.closePath();
    ctx.fill();
    // Thumb
    ctx.beginPath();
    ctx.moveTo(cx + s * 0.35, cy - s * 0.1);
    ctx.quadraticCurveTo(cx + s * 0.7, cy - s * 0.2, cx + s * 0.6, cy + s * 0.1);
    ctx.lineTo(cx + s * 0.35, cy + s * 0.1);
    ctx.fill();
  // ── Material previews ──────────────────────────────────────────────
  } else if (name.includes("wood") || name.includes("oak")) {
    // Log cross-section
    ctx.beginPath();
    ctx.arc(cx, cy, s * 0.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = adjustAlpha(c, 0.4);
    ctx.beginPath();
    ctx.arc(cx, cy, s * 0.4, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, s * 0.2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = c;
  } else if (name.includes("thread") || name.includes("silver")) {
    // Spool
    ctx.fillRect(cx - s * 0.15, cy - s * 0.6, s * 0.3, s * 1.2);
    ctx.beginPath();
    ctx.ellipse(cx, cy - s * 0.6, s * 0.35, s * 0.15, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(cx, cy + s * 0.6, s * 0.35, s * 0.15, 0, 0, Math.PI * 2);
    ctx.fill();
    // Thread wrap
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(cx - s * 0.12, cy - s * 0.3 + i * s * 0.2);
      ctx.lineTo(cx + s * 0.12, cy - s * 0.3 + i * s * 0.2);
      ctx.stroke();
    }
  } else if (name.includes("stone") || name.includes("crystal") || name.includes("moonstone")) {
    // Faceted gem
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 0.8);
    ctx.lineTo(cx + s * 0.6, cy - s * 0.2);
    ctx.lineTo(cx + s * 0.4, cy + s * 0.6);
    ctx.lineTo(cx - s * 0.4, cy + s * 0.6);
    ctx.lineTo(cx - s * 0.6, cy - s * 0.2);
    ctx.closePath();
    ctx.fill();
    // Facet line
    ctx.strokeStyle = adjustAlpha(c, 0.4);
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.6, cy - s * 0.2);
    ctx.lineTo(cx, cy);
    ctx.lineTo(cx + s * 0.6, cy - s * 0.2);
    ctx.stroke();
    ctx.strokeStyle = c;
  } else if (name.includes("scale") || name.includes("dragon")) {
    // Dragon scale — overlapping diamond shapes
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 2; col++) {
        const ox = cx - s * 0.3 + col * s * 0.5 + (row % 2) * s * 0.25;
        const oy = cy - s * 0.5 + row * s * 0.35;
        ctx.beginPath();
        ctx.moveTo(ox, oy - s * 0.2);
        ctx.lineTo(ox + s * 0.2, oy);
        ctx.lineTo(ox, oy + s * 0.2);
        ctx.lineTo(ox - s * 0.2, oy);
        ctx.closePath();
        ctx.fill();
      }
    }
  } else {
    // Generic item — rounded square with inner highlight
    drawRoundRect(ctx, cx - s * 0.5, cy - s * 0.5, s, s, s * 0.2, c);
    ctx.fill();
    ctx.fillStyle = adjustAlpha(c, 0.4);
    drawRoundRect(ctx, cx - s * 0.3, cy - s * 0.3, s * 0.6, s * 0.6, s * 0.12, undefined);
    ctx.fill();
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function adjustAlpha(hex: string, alpha: number): string {
  // Handle both #hex and rgba() inputs
  if (hex.indexOf("rgba") === 0) {
    // Replace existing alpha
    return hex.replace(/,\s*[\d.]+\)$/, `,${alpha})`);
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ─── Self-Register ──────────────────────────────────────────────────────────

registerApp(catalogApp);

export { catalogApp };
